/**
 * isometricProjection.js
 * ----------------------------------------------------------------------------
 * PSEUDO-PERSPECTIVA ISOMÉTRICA da mesma geometria do desenho em planta —
 * a vista "DETALHE MARMORARIA" das pranchas de arquitetura (peça extrudada
 * com espessura, recortes no tampo, cotas e chamadas com líder).
 *
 * A cena HERDA O LAYOUT DA MONTAGEM: cada peça entra na posição salva na
 * planta (`position` em % do canvas) e com a sua rotação — a saia colada na
 * lateral esquerda aparece na lateral esquerda da perspectiva, como a peça
 * final. (Antes as peças eram enfileiradas lado a lado.)
 *
 * Projeção clássica: iso(x, y, z) = ((x − y)·cos30°, (x + y)·sin30° − z)
 *  - x: comprimento (direita), y: profundidade, z: altura (espessura)
 *
 * SOMENTE LEITURA: gera uma cena declarativa (polígonos/linhas/labels) que o
 * IsometricView renderiza. Nenhuma edição acontece na isométrica.
 */

import { getOutlinePointsMeters, formatMeasure } from "./measurementUtils";
import { getCutoutSize, isCircularCutout } from "./cutoutUtils";
import { DEFAULT_METERS_TO_PX, pxToMeters } from "./scaleUtils";
import { WALL_THICKNESS_M } from "./wallUtils";

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;

/** Espessura visual padrão da chapa (3cm). */
export const DEFAULT_SLAB_THICKNESS = 0.01;

/** Altura da PAREDE na perspectiva: só um "coto" de referência baixo — o
 * objetivo é mostrar o formato/encaixe, não uma parede em elevação real
 * (que dominaria a cena e sairia do enquadramento). */
export const ISO_WALL_HEIGHT_M = 0.32;

/** Projeta um ponto 3D (metros) no plano isométrico (unidades de metro). */
export function isoProject(x, y, z = 0) {
  return { x: (x - y) * COS30, y: (x + y) * SIN30 - z };
}

/**
 * Monta a cena isométrica a partir das peças normalizadas, preservando o
 * layout da planta.
 *
 * @param {Array} pieces peças normalizadas (isStone) com cutouts/shape
 * @param {object} opts { thickness, planWidth, planHeight } — planWidth/Height
 *                 são as dimensões LÓGICAS (px) do canvas da planta, usadas
 *                 para converter `position` (%) em metros.
 * @returns {{ polygons, lines, labels, dims, bbox }} coords em "metros projetados"
 */
export function buildIsometricScene(pieces, opts = {}) {
  const planWMeters = (opts.planWidth || 760) / DEFAULT_METERS_TO_PX;
  const planHMeters = (opts.planHeight || 480) / DEFAULT_METERS_TO_PX;
  const scene = { polygons: [], lines: [], labels: [], dims: [], bbox: null };
  const stones = (pieces || []).filter(p => p.isStone && p.width > 0);
  const walls = (opts.walls || []).filter(
    el => el && el.shape === "bezier" && Array.isArray(el.points) && el.points.length >= 2
  );
  if (!stones.length && !walls.length) return scene;

  // PAREDES desenham primeiro (ficam atrás, como um pano de fundo — as
  // peças de pedra "descansam" na frente delas). Pontos vêm em PX de
  // conteúdo (mesmo espaço da planta 2D); convertidos direto pra metros
  // (a mesma base de 120px/m usada em toda a planta — `position%` das
  // peças já cai nessa base via planWMeters/planHMeters).
  walls.forEach(wall => buildWall(scene, wall));

  // posição do CENTRO de cada peça em metros do plano
  const placed = stones.map(piece => {
    const pos = piece.position || { x: 50, y: 50 };
    return {
      piece,
      cx: ((Number(pos.x) || 50) / 100) * planWMeters,
      cy: ((Number(pos.y) || 50) / 100) * planHMeters
    };
  });
  // lookup por fichaId: permite que uma peça vinculada (saia/frontão) ancore
  // a linha de junção na borda REAL da peça-mãe (ver buildVerticalPiece),
  // em vez de confiar na própria `position` — que no editor 2D às vezes tem
  // um pequeno respiro de propósito (legibilidade), e essa folga não deve
  // "vazar" pra 3D como uma junta com 2-3 linhas soltas.
  const byFichaId = new Map();
  placed.forEach(entry => {
    if (entry.piece.fichaId) byFichaId.set(entry.piece.fichaId, entry);
  });

  // QUINA entre 2 peças verticais (2 saias/frontões que se encontram, ex.:
  // balcão em L com saia na frente E na lateral): cada uma, sozinha, fecha
  // a ponta livre com uma "tampa" cujo canto externo (A2/B2) é a própria
  // borda deslocada pela ESPESSURA na direção perpendicular DELA — quando
  // duas pontas coincidem (mesmo canto), cada peça desloca pra um lado
  // diferente, e o canto externo abre um vão (as duas faces não se
  // encontram) em vez de fechar limpo. Pré-calcula a junta + normal de
  // cada peça vertical, detecta pontas coincidentes e calcula o PONTO DE
  // QUINA (miter — interseção das duas retas deslocadas, mesmo princípio
  // usado no acabamento da parede) pra usar no lugar do deslocamento
  // "reto" de cada peça — fecha o vão sem sobrepor.
  const verticalSeams = placed
    .map(entry => {
      const relation = entry.piece.relacaoPrincipal || "neutra";
      if (relation !== "curva_baixo" && relation !== "curva_cima") return null;
      const T = pieceLocalTransform(entry.piece, entry.cx, entry.cy);
      const { A, B } = computeVerticalSeam(entry.piece, T, byFichaId);
      const ddx = B[0] - A[0];
      const ddy = B[1] - A[1];
      const len = Math.hypot(ddx, ddy) || 1;
      let nx = ddy / len;
      let ny = -ddx / len;
      if (nx + ny < 0) {
        nx = -nx;
        ny = -ny;
      }
      return {
        fichaId: entry.piece.fichaId,
        A,
        B,
        nx,
        ny,
        thickness: getPieceThickness(entry.piece, opts.thickness)
      };
    })
    .filter(Boolean);
  const CORNER_EPS = 0.03; // 3cm — mesmo canto, na prática
  // "fichaId:A" / "fichaId:B" -> ponto externo em miter (substitui o
  // deslocamento reto dessa peça naquela ponta) + marca a tampa a pular.
  const outerCorner = new Map();
  for (let i = 0; i < verticalSeams.length; i++) {
    for (let j = i + 1; j < verticalSeams.length; j++) {
      const vi = verticalSeams[i];
      const vj = verticalSeams[j];
      const combos = [
        ["B", vi, "A", vj],
        ["B", vi, "B", vj],
        ["A", vi, "A", vj],
        ["A", vi, "B", vj]
      ];
      combos.forEach(([endI, seamI, endJ, seamJ]) => {
        const pi = endI === "A" ? seamI.A : seamI.B;
        const pj = endJ === "A" ? seamJ.A : seamJ.B;
        if (Math.hypot(pi[0] - pj[0], pi[1] - pj[1]) > CORNER_EPS) return;
        // bissetriz das 2 normais + comprimento de miter (1/cos(meio-ângulo),
        // com limite pra não disparar um bico enorme em ângulos muito agudos)
        let bx = seamI.nx + seamJ.nx;
        let by = seamI.ny + seamJ.ny;
        const blen = Math.hypot(bx, by);
        const avgThickness = (seamI.thickness + seamJ.thickness) / 2;
        let outerPt;
        if (blen < 1e-4) {
          // normais opostas (raro aqui): sem bissetriz útil, usa a média
          outerPt = [
            pi[0] + (seamI.nx + seamJ.nx) * avgThickness * 0.5,
            pi[1] + (seamI.ny + seamJ.ny) * avgThickness * 0.5
          ];
        } else {
          bx /= blen;
          by /= blen;
          const cosHalf = Math.max(bx * seamI.nx + by * seamI.ny, 0.2);
          const miterLen = avgThickness / cosHalf;
          outerPt = [pi[0] + bx * miterLen, pi[1] + by * miterLen];
        }
        if (seamI.fichaId) outerCorner.set(`${seamI.fichaId}:${endI}`, outerPt);
        if (seamJ.fichaId) outerCorner.set(`${seamJ.fichaId}:${endJ}`, outerPt);
      });
    }
  }

  // algoritmo do pintor: peças mais "ao fundo" (menor x+y) desenham primeiro
  placed.sort((a, b) => a.cx + a.cy - (b.cx + b.cy));

  placed.forEach(entry =>
    buildPiece(
      scene,
      entry.piece,
      entry.cx,
      entry.cy,
      getPieceThickness(entry.piece, opts.thickness),
      byFichaId,
      outerCorner
    )
  );

  scene.bbox = computeBbox(scene);
  return scene;
}

function getPieceThickness(piece, fallback) {
  const raw =
    piece.slabThicknessM ??
    piece.espessuraM ??
    piece.slabThickness ??
    piece.slabThicknessMm ??
    piece.espessura_mm ??
    piece.espessuraMm ??
    piece.grossura_mm ??
    piece.grossuraMm ??
    fallback;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_SLAB_THICKNESS;
  return value > 0 && value < 1 ? value : value / 1000;
}

/** Transform local (canto sup-esq da peça) -> plano, com rotação em torno
 * do centro. Fatorado pra poder ser recalculado tanto pra peça quanto,
 * quando preciso, pra peça-mãe dela (ver buildVerticalPiece). */
function pieceLocalTransform(piece, centerX, centerY) {
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const rot = (((piece.rotation || 0) % 360) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return (lx, ly) => {
    const dx = lx - W / 2;
    const dy = ly - H / 2;
    return [centerX + dx * cos - dy * sin, centerY + dx * sin + dy * cos];
  };
}

/**
 * Calcula os 2 pontos da "linha de junção" (costura) de uma peça VERTICAL
 * (saia/frontão) com a peça-mãe — ANCORADA na borda real da mãe quando
 * achada (ver comentário em `buildVerticalPiece`), com fallback pra própria
 * posição da peça vinculada quando não há mãe (dados antigos sem fichaId).
 * Extraído numa função à parte pra poder ser pré-calculado (detecção de
 * quina entre 2 peças verticais, em `buildIsometricScene`) ANTES de montar
 * os polígonos de verdade.
 */
function computeVerticalSeam(piece, T, byFichaId) {
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const edge = piece.linkedTo && piece.linkedTo.edge;
  const seamY =
    edge === "front" || edge === "left"
      ? 0
      : edge === "back" || edge === "right"
        ? H
        : H / 2;
  let [x1, y1] = T(0, seamY);
  let [x2, y2] = T(W, seamY);

  // ANCORA a junta na borda REAL da peça-mãe (se achada), em vez de confiar
  // só na `position` salva da peça vinculada — no editor 2D o copilot/form
  // deixa um pequeno respiro de propósito (legibilidade das duas peças
  // separadas no plano), e essa folga não deve "vazar" pra dentro da
  // perspectiva 3D como uma junta com várias linhas soltas em vez de 1 só.
  const parentEntry =
    edge && piece.linkedTo && piece.linkedTo.parentId && byFichaId
      ? byFichaId.get(piece.linkedTo.parentId)
      : null;
  if (parentEntry && ["front", "back", "left", "right"].includes(edge)) {
    const parent = parentEntry.piece;
    const parentT = pieceLocalTransform(parent, parentEntry.cx, parentEntry.cy);
    const pW = parent.width;
    const pH = parent.height && parent.height > 0.1 ? parent.height : 0.1;
    const edgeLocalPts =
      edge === "front"
        ? [[0, pH], [pW, pH]]
        : edge === "back"
          ? [[0, 0], [pW, 0]]
          : edge === "left"
            ? [[0, 0], [0, pH]]
            : [[pW, 0], [pW, pH]]; // right
    const [pp0, pp1] = edgeLocalPts.map(([lx, ly]) => parentT(lx, ly));
    // casa pp0/pp1 com x1,y1/x2,y2 por proximidade — preserva a direção
    // que o resto da função espera, sem depender da convenção de rotação
    const d0 = Math.hypot(pp0[0] - x1, pp0[1] - y1);
    const d1 = Math.hypot(pp1[0] - x1, pp1[1] - y1);
    if (d0 <= d1) {
      [x1, y1] = pp0;
      [x2, y2] = pp1;
    } else {
      [x1, y1] = pp1;
      [x2, y2] = pp0;
    }
  }
  return { A: [x1, y1], B: [x2, y2] };
}

function buildPiece(scene, piece, centerX, centerY, thickness, byFichaId, outerCorner) {
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const T = pieceLocalTransform(piece, centerX, centerY);

  // RELAÇÃO COM A PRINCIPAL: muda como a peça aparece na perspectiva —
  // saia cai curvada para baixo, frontão sobe, sobrepostas deslocam no
  // eixo z. Vem do cadastro do insumo (relacao_principal) e vale para
  // QUALQUER peça que a tenha (criada pelo copilot OU pelo formulário).
  const relation = piece.relacaoPrincipal || "neutra";
  if (relation === "curva_baixo" || relation === "curva_cima") {
    buildVerticalPiece(scene, piece, T, W, H, thickness, relation, byFichaId, outerCorner);
    return;
  }
  let zBase = 0;
  if (relation === "sobreposta_acima") zBase = thickness;
  if (relation === "sobreposta_abaixo") zBase = -thickness;

  const outline = getOutlinePointsMeters(piece).map(([lx, ly]) => T(lx, ly));

  const top = outline.map(([x, y]) => isoProject(x, y, zBase + thickness));
  // FACES LATERAIS visíveis: normal externa apontando para +x/+y (câmera)
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    const nx = b[1] - a[1];
    const ny = -(b[0] - a[0]);
    if (nx + ny <= 0.0001) continue; // face oculta
    const pa = isoProject(a[0], a[1], zBase + thickness);
    const pb = isoProject(b[0], b[1], zBase + thickness);
    const pa0 = isoProject(a[0], a[1], zBase);
    const pb0 = isoProject(b[0], b[1], zBase);
    scene.polygons.push({ kind: "side", points: [pa, pb, pb0, pa0] });
  }
  // TAMPO por cima das laterais
  scene.polygons.push({ kind: "top", points: top });

  // RECORTES projetados no tampo (seguem a rotação da peça)
  (piece.cutouts || []).forEach(cutout => {
    const size = getCutoutSize(cutout);
    let outlinePts;
    if (isCircularCutout(cutout)) {
      outlinePts = [];
      const r = size.w / 2;
      for (let i = 0; i < 24; i++) {
        const ang = (i / 24) * Math.PI * 2;
        const [px, py] = T(
          cutout.cx + Math.cos(ang) * r,
          cutout.cy + Math.sin(ang) * r
        );
        outlinePts.push(isoProject(px, py, zBase + thickness));
      }
    } else {
      outlinePts = [
        T(cutout.cx - size.w / 2, cutout.cy - size.h / 2),
        T(cutout.cx + size.w / 2, cutout.cy - size.h / 2),
        T(cutout.cx + size.w / 2, cutout.cy + size.h / 2),
        T(cutout.cx - size.w / 2, cutout.cy + size.h / 2)
      ].map(([px, py]) => isoProject(px, py, zBase + thickness));
    }
    scene.polygons.push({ kind: "cutout", points: outlinePts });

    // chamada com líder pontilhado (estilo "• CUBA")
    if (cutout.label) {
      const [ax, ay] = T(cutout.cx, cutout.cy);
      const anchor = isoProject(ax, ay, zBase + thickness);
      const tip = { x: anchor.x + 0.28, y: anchor.y - 0.34 };
      scene.lines.push({ kind: "leader", from: anchor, to: tip });
      scene.labels.push({
        kind: "callout",
        x: tip.x + 0.03,
        y: tip.y - 0.02,
        text: cutout.label
      });
    }
  });

  // CHAMADAS de borda (ex.: RODA BASE EM PEDRA)
  const edgeLabels = piece.edgeLabels || {};
  if (edgeLabels.front) {
    const [mx, my] = T(W / 2, H);
    const midFront = isoProject(mx, my, zBase + thickness / 2);
    const tip = { x: midFront.x + 0.25, y: midFront.y + 0.3 };
    scene.lines.push({ kind: "leader", from: midFront, to: tip });
    scene.labels.push({
      kind: "callout",
      x: tip.x + 0.03,
      y: tip.y + 0.04,
      text: edgeLabels.front
    });
  }

  // COTAS: comprimento (frente) e largura (lado direito), no frame da peça
  const off = 0.18;
  const [fax, fay] = T(0, H + off);
  const [fbx, fby] = T(W, H + off);
  const fA = isoProject(fax, fay, 0);
  const fB = isoProject(fbx, fby, 0);
  scene.dims.push({
    line: { from: fA, to: fB },
    label: {
      x: (fA.x + fB.x) / 2,
      y: (fA.y + fB.y) / 2 + 0.12,
      text: `${formatMeasure(piece.width)} m`
    }
  });
  const [rax, ray] = T(W + off, 0);
  const [rbx, rby] = T(W + off, H);
  const rA = isoProject(rax, ray, 0);
  const rB = isoProject(rbx, rby, 0);
  scene.dims.push({
    line: { from: rA, to: rB },
    label: {
      x: (rA.x + rB.x) / 2 + 0.14,
      y: (rA.y + rB.y) / 2,
      text: `${formatMeasure(piece.height)} m`
    }
  });

  // rótulo da peça sobre o tampo
  const [lx, ly] = T(W / 2, H / 2);
  const center = isoProject(lx, ly, zBase + thickness);
  scene.labels.push({
    kind: "piece",
    x: center.x,
    y: center.y,
    text: `${formatMeasure(piece.width)} × ${formatMeasure(piece.height)} m`
  });
}

/**
 * Peça VERTICAL (relação "curvada para baixo/cima"): a saia não é uma chapa
 * deitada — ela desce (ou sobe, no frontão) a partir da linha da borda da
 * peça-mãe. Renderiza uma face vertical ao longo do eixo longitudinal do
 * strip da planta, com altura = largura real da peça.
 */
function buildVerticalPiece(scene, piece, T, W, H, thickness, relation, byFichaId, outerCorner) {
  const drop = Math.max(piece.height || 0.1, 0.02); // altura real (largura)
  const { A: [x1, y1], B: [x2, y2] } = computeVerticalSeam(piece, T, byFichaId);
  const parentEntry =
    piece.linkedTo && piece.linkedTo.parentId && byFichaId
      ? byFichaId.get(piece.linkedTo.parentId)
      : null;
  // Uma saia/frontao realmente vinculada pertence visualmente ao mesmo
  // conjunto da principal. Sua aresta interna nasce na aresta da mae, mas a
  // face externa continua deslocada pela espessura real da chapa.
  const seamlessLinked = !!parentEntry;

  // ESPESSURA: a placa vertical é extrudada pela espessura da chapa —
  // face externa deslocada pela normal (apontando para a câmera), topo
  // visto de cima e tampa na extremidade (antes era um plano sem espessura)
  const ddx = x2 - x1;
  const ddy = y2 - y1;
  const len = Math.hypot(ddx, ddy) || 1;
  let nx = ddy / len;
  let ny = -ddx / len;
  if (nx + ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  const A = [x1, y1];
  const B = [x2, y2];
  // canto externo: em ponta LIVRE é só a própria borda deslocada pela
  // espessura; numa ponta que ENCONTRA outra peça vertical (quina de um
  // balcão em L, por ex.), usa o ponto de MITER pré-calculado em
  // `buildIsometricScene` (interseção das 2 retas deslocadas) — sem isso,
  // cada peça desloca pro seu próprio lado e abre um vão na quina.
  const aKey = piece.fichaId ? `${piece.fichaId}:A` : null;
  const bKey = piece.fichaId ? `${piece.fichaId}:B` : null;
  const aShared = outerCorner && aKey && outerCorner.has(aKey);
  const bShared = outerCorner && bKey && outerCorner.has(bKey);
  const A2 = aShared ? outerCorner.get(aKey) : [x1 + nx * thickness, y1 + ny * thickness];
  const B2 = bShared ? outerCorner.get(bKey) : [x2 + nx * thickness, y2 + ny * thickness];

  // curva_baixo: da base da chapa (z=0) para baixo; curva_cima: do tampo
  // (z=thickness) para cima
  const joinZ = seamlessLinked
    ? getPieceThickness(parentEntry.piece, thickness)
    : thickness;
  const zTop =
    relation === "curva_baixo"
      ? seamlessLinked
        ? joinZ
        : 0
      : joinZ + drop;
  const zBottom =
    relation === "curva_baixo"
      ? (seamlessLinked ? joinZ : 0) - drop
      : joinZ;
  const p = (pt, z) => isoProject(pt[0], pt[1], z);
  const faceA = A2;
  const faceB = B2;

  // Face externa da chapa, preservando a espessura tambem nas vinculadas.
  scene.polygons.push({
    kind: "side",
    points: [p(faceA, zTop), p(faceB, zTop), p(faceB, zBottom), p(faceA, zBottom)]
  });
  // TAMPA da extremidade B: só quando é uma ponta "livre" de verdade — numa
  // quina (B2 já veio do miter acima) a própria face externa das duas
  // peças fecha o canto; uma tampa aqui ficaria torta (não é mais
  // perpendicular ao trecho) e sobraria por cima da peça vizinha.
  if (!bShared) {
    scene.polygons.push({
      kind: "side",
      points: [p(B, zTop), p(B2, zTop), p(B2, zBottom), p(B, zBottom)]
    });
  }
  scene.polygons.push({
    kind: "top",
    points: [p(A, zTop), p(B, zTop), p(B2, zTop), p(A2, zTop)]
  });
  // Na vinculada, A→B coincide exatamente com a borda da principal; A2→B2
  // fica visível como o limite externo da espessura. Assim a emenda não é
  // duplicada, mas a placa continua tendo volume.

  // rótulo no meio da face externa
  const faceTopA = p(faceA, zTop);
  const faceBotB = p(faceB, zBottom);
  scene.labels.push({
    kind: "piece",
    x: (faceTopA.x + faceBotB.x) / 2,
    y: (faceTopA.y + faceBotB.y) / 2,
    text: `${formatMeasure(piece.width)} × ${formatMeasure(drop)} m`
  });

  // cotas: comprimento ao longo da face externa + altura na extremidade
  const dimZ = relation === "curva_baixo" ? zBottom - 0.06 : zTop + 0.06;
  const dA = p(faceA, dimZ);
  const dB = p(faceB, dimZ);
  scene.dims.push({
    line: { from: dA, to: dB },
    label: {
      x: (dA.x + dB.x) / 2,
      y: (dA.y + dB.y) / 2 + (relation === "curva_baixo" ? 0.12 : -0.08),
      text: `${formatMeasure(piece.width)} m`
    }
  });
  const hTop = p(faceA, zTop);
  const hBot = p(faceA, zBottom);
  scene.dims.push({
    line: { from: hTop, to: hBot },
    label: {
      x: hBot.x - 0.16,
      y: (hTop.y + hBot.y) / 2,
      text: `${formatMeasure(drop)} m`
    }
  });
}

/**
 * Deslado (offset) de uma polilinha por uma distância constante, com a
 * quina em vértices internos calculada por MITER (interseção das duas
 * retas deslocadas) — mesmo princípio do `stroke-linejoin: miter` do SVG,
 * só que calculado à mão porque aqui o resultado vira um POLÍGONO 3D
 * (extrusão), não um `<path>` que o navegador possa estrofar sozinho.
 * Sem isso, cada trecho vira uma caixa independente e as caixas vizinhas
 * se sobrepõem na quina — o efeito "esfacelado" (contornos duplicados se
 * cruzando) que aparecia antes.
 */
function offsetPolyline(points, dist) {
  const n = points.length;
  if (n < 2) return points.slice();
  const normals = [];
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const len = Math.hypot(dx, dy) || 1;
    normals.push({ x: -dy / len, y: dx / len });
  }
  const out = [];
  for (let i = 0; i < n; i++) {
    if (i === 0) {
      const nrm = normals[0];
      out.push({ x: points[0].x + nrm.x * dist, y: points[0].y + nrm.y * dist });
    } else if (i === n - 1) {
      const nrm = normals[n - 2];
      out.push({ x: points[i].x + nrm.x * dist, y: points[i].y + nrm.y * dist });
    } else {
      const n1 = normals[i - 1];
      const n2 = normals[i];
      let bx = n1.x + n2.x;
      let by = n1.y + n2.y;
      const blen = Math.hypot(bx, by);
      if (blen < 1e-6) {
        // trechos praticamente opostos (dobra de ~180°): sem bissetriz útil
        out.push({ x: points[i].x + n1.x * dist, y: points[i].y + n1.y * dist });
        continue;
      }
      bx /= blen;
      by /= blen;
      const cosHalf = Math.max(bx * n1.x + by * n1.y, 0.2); // miter-limit ~5x
      const miterLen = dist / cosHalf;
      out.push({ x: points[i].x + bx * miterLen, y: points[i].y + by * miterLen });
    }
  }
  return out;
}

/**
 * PAREDE (utility:wall) na perspectiva: um "coto" baixo extrudado ao longo
 * de toda a parede — só pra mostrar o formato/encaixe do ambiente, igual
 * ao traço de referência da planta. Não tem cota, chamada ou rótulo (não é
 * item cobrado). O contorno (topo + laterais) é UM SÓ deslocamento com
 * quina em miter — as quinas ficam com aresta única, sem sobreposição.
 */
function buildWall(scene, wall) {
  const pts = wall.points.map(p => ({
    x: pxToMeters(p.x, 1),
    y: pxToMeters(p.y, 1)
  }));
  if (pts.length < 2) return;
  const half = WALL_THICKNESS_M / 2;
  const height = ISO_WALL_HEIGHT_M;
  const p = (pt, z) => isoProject(pt.x, pt.y, z);

  const left = offsetPolyline(pts, half);
  const right = offsetPolyline(pts, -half);

  // topo: UM polígono contínuo (ida pela esquerda, volta pela direita) —
  // sem seams internas nas quinas.
  scene.polygons.push({
    kind: "wall-top",
    points: [
      ...left.map(pt => p(pt, height)),
      ...right.slice().reverse().map(pt => p(pt, height))
    ]
  });

  // laterais + tampas das pontas: por trecho, mas usando os pontos JÁ
  // mitrados — trechos vizinhos compartilham a mesma aresta, sem sobrepor.
  for (let i = 0; i < pts.length - 1; i++) {
    scene.polygons.push({
      kind: "wall-side",
      points: [p(left[i], height), p(left[i + 1], height), p(left[i + 1], 0), p(left[i], 0)]
    });
    scene.polygons.push({
      kind: "wall-side",
      points: [p(right[i], height), p(right[i + 1], height), p(right[i + 1], 0), p(right[i], 0)]
    });
  }
  scene.polygons.push({
    kind: "wall-side",
    points: [p(left[0], height), p(right[0], height), p(right[0], 0), p(left[0], 0)]
  });
  const last = pts.length - 1;
  scene.polygons.push({
    kind: "wall-side",
    points: [p(left[last], height), p(right[last], height), p(right[last], 0), p(left[last], 0)]
  });
}

function computeBbox(scene) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const eat = pt => {
    minX = Math.min(minX, pt.x);
    minY = Math.min(minY, pt.y);
    maxX = Math.max(maxX, pt.x);
    maxY = Math.max(maxY, pt.y);
  };
  scene.polygons.forEach(poly => poly.points.forEach(eat));
  scene.lines.forEach(l => {
    eat(l.from);
    eat(l.to);
  });
  scene.labels.forEach(eat);
  scene.dims.forEach(d => {
    eat(d.line.from);
    eat(d.line.to);
    eat(d.label);
  });
  if (!Number.isFinite(minX)) return null;
  const margin = 0.25;
  return {
    x: minX - margin,
    y: minY - margin,
    w: maxX - minX + margin * 2,
    h: maxY - minY + margin * 2
  };
}
